import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";

import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { useForm, Controller } from "react-hook-form";

import ModalSplit from "@Modals/layout/ModalSplit";
import FormSimple from "@components/formGroup/formSimple";
import FormTextarea from "@components/formGroup/formTextarea";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import InputFileMultipleArh from "@components/input/InputFileMultiple";
import { TextArea, ListboxSimple } from "@components/tailwindComponents";

import { IndividualObjectiveScoreType } from "@helpers/_constants";
import {
  IndividualObjective,
  IndividualObjectivePeriod,
  IndividualObjectiveType
} from "@services/developmentPlan";
import { useMultipleFileUpload } from "@hooks/index";
import { convertToFormData } from "@helpers/_functions";

const SCORE_TYPE_LIST = Object.values(IndividualObjectiveScoreType).map(
  (value) => ({ _id: value, name: value })
);

function ModalUpsertIndividualObjective(props) {
  const { onClickClose, isOpen, individualObjective, isMyEmployeeSpace } =
    props;

  const { t } = useTranslation("assesment.frequency");

  const { individualObjectiveType, individualObjectivePeriod } =
    useReduxSelector(({ developmentPlan }) => developmentPlan);

  const enterpriseId = useReduxSelector(
    ({ settings }) => settings.enterprise._id
  );

  const { control, handleSubmit, reset } = useForm();

  const defaultFiles = Array.isArray(individualObjective?.files)
    ? individualObjective.files
    : individualObjective?.file
      ? [individualObjective.file]
      : [];

  const {
    oldFiles,
    handleDeleteOldFiles,
    handleUploadStatusChange,
    resetInputFiles,
    removeOldFiles,
    toDeleteOldFiles,
    allFileDone
  } = useMultipleFileUpload({
    defaultFiles
  });

  const { showError, showSuccess } = useNotification();
  const [isSaving, setIsSaving] = useState(false);

  const formatIndividualObjective = useCallback(() => {
    const objectivePeriod = individualObjectivePeriod.list.find(
      (iop) => iop._id === individualObjective.objectivePeriod
    );
    const formattedObjectivePeriod = objectivePeriod
      ? {
          id: objectivePeriod._id,
          name: objectivePeriod.name,
          value: objectivePeriod.name
        }
      : null;

    const objectiveType = individualObjectiveType.list.find(
      (iot) => iot._id === individualObjective.objectiveType?._id
    );
    const formattedObjectiveType = objectiveType
      ? {
          id: objectiveType._id,
          name: objectiveType.name,
          value: objectiveType.name
        }
      : null;

    const scoreType = SCORE_TYPE_LIST.find(
      (st) => st._id === individualObjective.scoreType
    );

    return {
      designation: individualObjective.designation,
      comment: individualObjective.comment,
      managerNote: individualObjective.managerNote || "",
      objectivePeriod: formattedObjectivePeriod,
      objectiveType: formattedObjectiveType,
      scoreType,
      files: []
    };
  }, [individualObjective]);

  useEffect(() => {
    if (isOpen) {
      setIsSaving(false);
      resetInputFiles();

      if (individualObjective) {
        reset(formatIndividualObjective());
      } else {
        reset({
          designation: "",
          comment: "",
          managerNote: "",
          objectivePeriod: "",
          objectiveType: "",
          scoreType: "",
          files: []
        });
      }
    }
  }, [isOpen, formatIndividualObjective]);

  async function handleTypeAdd(typeName) {
    const sameName = individualObjectiveType.list.find(
      (iot) => iot.name === typeName
    );
    if (sameName) {
      showError("deja_existante", { object: "objective_type" });
      return;
    }

    try {
      await IndividualObjectiveType.insert({
        name: typeName,
        Enterprise: enterpriseId
      });
    } catch (error) {
      console.error(error);
      showError("error");
    }
  }

  async function handleTypeDelete(individualObjectiveTypeId) {
    try {
      await IndividualObjectiveType.deleteById(individualObjectiveTypeId);
    } catch (error) {
      console.error(error);
      showError("error");
    }
  }

  async function handlePeriodAdd(periodName) {
    const sameName = individualObjectivePeriod.list.find(
      (iop) => iop.name === periodName
    );
    if (sameName) {
      showError("deja_existante", { object: "period" });
      return;
    }

    try {
      await IndividualObjectivePeriod.insert({
        name: periodName,
        Enterprise: enterpriseId
      });
    } catch (error) {
      console.error(error);
      showError("error");
    }
  }

  async function handlePeriodDelete(individualObjectivePeriodId) {
    try {
      await IndividualObjectivePeriod.deleteById(individualObjectivePeriodId);
    } catch (error) {
      console.error(error);
      if (error.message.includes("400")) {
        showError("already_used", {
          object1: "this_period",
          object2: "objective"
        });
      } else {
        showError("error");
      }
    }
  }

  async function handleSuccess(formValue) {
    try {
      setIsSaving(true);
      const { files = [], ...formData } = formValue;

      if (toDeleteOldFiles?.length) {
        await removeOldFiles();
      }

      const baseData = {
        ...formData,
        file: files,
        objectivePeriod: formData.objectivePeriod.id,
        objectiveType: formData.objectiveType.id,
        scoreType: formData.scoreType._id,
        lastUpdate: moment().format("YYYY-MM-DD")
      };

      if (individualObjective?._id) {
        const updateData = {
          _id: individualObjective._id,
          ...baseData,
          Enterprise: individualObjective.Enterprise
        };

        const payload = convertToFormData(updateData);
        // @ts-ignore
        payload._id = updateData._id;

        await IndividualObjective.updateById(payload);
        showSuccess("update_action_success", { object: "objective" });
      } else {
        const insertData = {
          ...baseData,
          score: 0,
          assignedEmployee: props.selectedEmployee,
          Enterprise: enterpriseId,
          employeeVisibility: isMyEmployeeSpace ? true : false
        };
        
        await IndividualObjective.insert(convertToFormData(insertData));
        showSuccess("creation_action_success", { object: "objective" });
      }

      props.onClickClose();
    } catch (error) {
      console.error(error);
      setIsSaving(false);
      showError("error");
    }
  }

  function handleError() {
    showError("field_obligatory");
  }

  const leftContent = (
    <div className="arh-relative arh-mt-9">
      <Controller
        control={control}
        name="designation"
        defaultValue=""
        rules={{ validate: { required: (value) => value.trim() !== "" } }}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { invalid }
        }) => (
          <FormSimple
            label={`${t("Désignation")}`}
            placeholder={t("Désignation")}
            ParentForm="arh-mb-2"
            classLabel="arh-flex"
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            invalid={invalid}
          />
        )}
      />

      <Controller
        control={control}
        name="objectiveType"
        defaultValue=""
        rules={{ required: true }}
        render={({
          field: { onChange, value: selectedTypeDataSet, name },
          fieldState: { invalid }
        }) => (
          <div className="arh-mb-2 arh-w-full">
            <label
              className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${
                !invalid ? "arh-text-black" : "arh-text-invalidColorTextInput"
              }`}
            >
              {t("Type")}
            </label>
            <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
              <InputWithAddOptions
                defaultText={t("Type")}
                optionsList={individualObjectiveType.list}
                secondDefaultText={t("Type")}
                name={name}
                optionListLabel="name"
                value={selectedTypeDataSet?.value}
                onChange={(event) => {
                  onChange(event?.target?.dataset || null);
                }}
                showAction={true}
                addItem={(newItem) => handleTypeAdd(newItem.name)}
                deleteItem={(toDeleteItem) =>
                  handleTypeDelete(toDeleteItem._id)
                }
                hideEdit={true}
                validateDropdown={invalid}
                showReset
              />
            </div>
          </div>
        )}
      />

      <Controller
        control={control}
        name="objectivePeriod"
        defaultValue=""
        rules={{ required: true }}
        render={({
          field: { onChange, value: selectedPeriodDataSet, name },
          fieldState: { invalid }
        }) => (
          <div className="arh-mb-2 arh-w-full">
            <label
              className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${
                !invalid ? "arh-text-black" : "arh-text-invalidColorTextInput"
              }`}
            >
              {t("Période visée")}
            </label>
            <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
              <InputWithAddOptions
                defaultText={t("Période visée")}
                optionsList={individualObjectivePeriod.list}
                secondDefaultText={t("Période visée")}
                name={name}
                optionListLabel="name"
                value={selectedPeriodDataSet?.value}
                onChange={(event) => {
                  onChange(event?.target?.dataset || null);
                }}
                addItem={(newItem) => handlePeriodAdd(newItem.name)}
                deleteItem={(toDeleteItem) =>
                  handlePeriodDelete(toDeleteItem._id)
                }
                showAction={true}
                hideEdit={true}
                showReset
                validateDropdown={invalid}
              />
            </div>
          </div>
        )}
      />

      <Controller
        control={control}
        name="scoreType"
        defaultValue=""
        rules={{ required: true }}
        render={({
          field: { onChange, value, name },
          fieldState: { invalid }
        }) => (
          <div className="arh-mb-2 arh-w-full">
            <label
              className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${
                !invalid ? "arh-text-black" : "arh-text-invalidColorTextInput"
              }`}
            >
              {t("Type de note/score")}
            </label>
            <div className="dropdown_content_hour dropdown_hoursWorking filter_posteAvailable arh-w-full">
              <ListboxSimple
                placeholder={t("Type de note")}
                name={name}
                optionList={SCORE_TYPE_LIST}
                selectedValue={value}
                setSelectedValue={onChange}
                invalid={invalid}
                customTranslator={t}
              />
            </div>
          </div>
        )}
      />

      <Controller
        control={control}
        name="files"
        defaultValue={[]}
        render={({
          field: { onChange, value },
          fieldState: { invalid }
        }) => (
          <div className="arh-mb-2 arh-w-full">
            <label
              className={`arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium ${
                !invalid ? "arh-text-black" : "arh-text-invalidColorTextInput"
              }`}
            >
              {t("Upload fichier")}
            </label>
            <div className="input_file_theme_employer">
              <InputFileMultipleArh
                handleChangeStatus={handleUploadStatusChange(value, onChange)}
                classUploadFile="arh-upload-createTask employer_theme_upload GreenThemeSelect"
                hideTitle={true}
                allFile={true}
                dataFile={oldFiles}
                onDeleteOldFile={handleDeleteOldFiles}
                isValid={invalid}
              />
            </div>
          </div>
        )}
      />
    </div>
  );

  const rightContent = (
    <div className="arh-relative arh-mt-4">
      <Controller
        control={control}
        name="comment"
        rules={{ validate: { required: (value) => value.trim() !== "" } }}
        render={({
          field: { onChange, value, name },
          fieldState: { invalid }
        }) => (
          <div className="arh-mb-3 arh-w-full">
            <TextArea
              label={t("Commentaires/explication")}
              placeholder={t("Commentaire")}
              additionnalClas="!arh-h-[150px] !arh-min-h-[150px] !arh-max-h-[150px] !arh-px-4 !arh-py-3 arh-resize-none"
              onChange={onChange}
              value={value}
              name={name}
              invalid={invalid}
            />
          </div>
        )}
      />

      <Controller
        control={control}
        name="managerNote"
        defaultValue=""
        render={({
          field: { onChange, value, name },
          fieldState: { invalid }
        }) => (
          <div className="arh-mb-2 arh-w-full">
            <FormTextarea
              label={t("Notes gestionnaire")}
              placeholder={t("Notes gestionnaire")}
              classTextarea="!arh-h-[150px] !arh-min-h-[150px] !arh-max-h-[150px] !arh-px-4 !arh-py-3"
              onChange={onChange}
              value={value}
              name={name}
              invalid={invalid}
            />
          </div>
        )}
      />
    </div>
  );

  return (
    <ModalSplit
      isOpen={isOpen}
      title={
        individualObjective ? t("Modifier un objectif") : t("Nouvel objectif")
      }
      onClickClose={onClickClose}
      isLoading={isSaving}
      widthType="split"
      buttonOptions={{
        click: handleSubmit(handleSuccess, handleError),
        color: "green",
        title: individualObjective ? t("Modifier") : t("Enregistrer")
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: t("Annuler")
      }}
      btnSaveDisabled={!allFileDone}
      left={leftContent}
      right={rightContent}
    />
  );
}

export default ModalUpsertIndividualObjective;
